import { useCallback, useEffect, useRef, useState } from 'react'

import { STATSIG_GATE_NAMES, useStatsigGate } from '@src/experiments/statsig-integration/use-statsig-gate.hook'
import { yieldToMain } from '@src/quince-site-common/src/utils/yieldToMain'

interface UseResponsiveActionOptions {
  /**
   * External "busy" signal (e.g. a shared cart-context flag) representing the same in-flight work.
   * OR-ed into both the re-entry guard and the returned `isPending`, so callers drive a single
   * loader from `isPending` and don't double-track state.
   */
  externalPending?: boolean
}

/**
 * Run an async CTA action (Add-to-Cart, Buy-Now, Checkout, …) with instant loading feedback, for INP.
 *
 * On `run(...)`: flips `isPending` URGENTLY so a spinner can paint within the click, `yieldToMain()`
 * so that paint flushes, then awaits the action — so the heavy work it triggers (often a synchronous
 * re-render of many consumers) lands AFTER the paint. `isPending` is cleared in a `finally`, so it is
 * deterministic across success, error, and early-return — no timeout, no handoff bookkeeping.
 *
 * Gated by the `ssr_responsive_cta` Statsig gate: gate OFF → `run()` invokes the action directly
 * (legacy behaviour, no optimistic pending), so it is a deploy-free kill-switch.
 *
 * @example
 *   const { isPending, run } = useResponsiveAction(onAddToCart, { externalPending: isAddToCartInProgress })
 *   <Button loading={isPending} onClick={() => run(isPreorder, planId)} />
 */
export function useResponsiveAction<TArgs extends unknown[]>(
  action: (...args: TArgs) => void | Promise<void>,
  options: UseResponsiveActionOptions = {},
): { isPending: boolean; run: (...args: TArgs) => void } {
  const { externalPending = false } = options
  const isResponsiveEnabled = useStatsigGate(STATSIG_GATE_NAMES.RESPONSIVE_CTA_LOADING_ENABLED)

  const [localPending, setLocalPending] = useState(false)
  const pendingRef = useRef(false)

  // The action runs after a yield, so the component may unmount while it is in flight. Guard the
  // post-await state update so we never call setLocalPending on an unmounted component.
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const run = useCallback(
    (...args: TArgs) => {
      // Legacy path (gate OFF / kill-switch): preserve master behaviour exactly — invoke the action
      // directly, with no optimistic pending and NO re-entry guard, so disabling the gate is a true
      // no-op vs. the pre-hook code. We deliberately do NOT wrap in Promise.resolve().catch(): the
      // pre-hook call site was a bare `onAddToCart(...)`, so a sync throw must propagate and a
      // rejected promise must surface as an unhandledrejection (Datadog RUM) exactly as before. The
      // `void` operator discards the void | Promise<void> return without catching it.
      if (!isResponsiveEnabled) {
        void action(...args)
        return
      }

      // Optimistic path only. Prevent duplicate execution: the ref closes the same-tick race that
      // React state alone cannot, and externalPending blocks re-entry while shared work is in flight.
      if (pendingRef.current || externalPending) {
        return
      }

      pendingRef.current = true
      setLocalPending(true)

      void (async () => {
        try {
          // Give the browser a chance to paint the loading state before any
          // expensive synchronous work triggered by the action.
          await yieldToMain()

          await action(...args)
        } catch {
          // Action is responsible for reporting its own failures.
        } finally {
          pendingRef.current = false
          if (isMountedRef.current) {
            setLocalPending(false)
          }
        }
      })()
    },
    [action, externalPending, isResponsiveEnabled],
  )

  return {
    isPending: localPending || externalPending,
    run,
  }
}
